import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import { LEGACY_REDIRECTS } from "../lib/legacyRedirects";

// Prefix/pattern rules for whole families of old-WordPress URLs (tag/category
// archives, author pages and RSS feeds) that don't warrant one-off map entries.
// First match wins; order matters (author before generic /feed).
const PATTERN_REDIRECTS = [
  [/^\/author(\/|$)/, "/team"],
  [/^\/tag(\/|$)/, "/blog"],
  [/^\/category(\/|$)/, "/blog"],
  [/\/feed\/?$/, "/blog"],
];

// Old WordPress/Elementor pagination keys — "_page" on its own and the hashed
// widget variants such as "38a773f2_page". They address pagination this site no
// longer has, so they only ever produce a duplicate of the clean URL.
const OBSOLETE_PARAM = /(^|_)page$/i;

// Strips the obsolete parameters, keeping any genuine ones. Returns null when
// there was nothing to strip, so a normal URL costs nothing.
export function cleanSearch(search) {
  if (!search || !/page=/i.test(search)) return null;
  const params = new URLSearchParams(search);
  let dropped = false;
  for (const key of Array.from(params.keys())) {
    if (OBSOLETE_PARAM.test(key)) {
      params.delete(key);
      dropped = true;
    }
  }
  if (!dropped) return null;
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

// Intercepts known legacy (old-WordPress) URLs and redirects to the current page.
// The static edge can't emit real 301s here, so this is a client-side redirect;
// paired with a self-referencing canonical on the destination it consolidates the
// old URLs onto the new architecture. Unknown paths fall through untouched.
export default function LegacyRedirect({ children }) {
  const { pathname, search } = useLocation();
  const key = pathname !== "/" ? pathname.replace(/\/+$/, "") : pathname;
  const target = LEGACY_REDIRECTS[key] || LEGACY_REDIRECTS[decodeURIComponent(key)];
  const cleaned = cleanSearch(search);

  if (target && target !== pathname) {
    // One hop: the destination is the final URL, already without the obsolete
    // parameters rather than carrying them on to be stripped afterwards.
    return <Navigate to={target + (cleaned !== null ? cleaned : search)} replace />;
  }
  for (const [re, dest] of PATTERN_REDIRECTS) {
    if (re.test(key) && dest !== pathname) {
      // Genuine parameters (utm_source and the like) travel with the visitor;
      // only the obsolete pagination keys are dropped.
      return <Navigate to={dest + (cleaned !== null ? cleaned : search)} replace />;
    }
  }
  // A live page reached with obsolete pagination parameters: same page, clean URL.
  // Where the path itself means nothing any more, the map and patterns above have
  // already had their say, so what's left is the page the visitor asked for.
  // A live page reached with a trailing slash or with obsolete pagination
  // parameters: the same page, on its canonical URL, in one hop.
  if (cleaned !== null || pathname !== key) {
    return <Navigate to={key + (cleaned !== null ? cleaned : search)} replace />;
  }
  return children;
}
